<template>
  <div :class="cn('flex flex-col grow overflow-hidden', props.class)">
    <div class="relative w-full z-0 flex flex-col grow overflow-y-auto">
      <Tabs
        v-show="!_searching"
        :key="tab"
        variant="linkedToSelector"
        :modelValue="tab"
        :hideNavigation="true"
        class="bg-primary grow overflow-y-auto"
      >
        <Tab
          v-if="allowedTypes.includes('space')"
          :label="$t('components.link_to_selector.type.spaces')"
        >
          <LinksTree
            type="space"
            :modelValue="modelValue"
            :buildingId="buildingId"
            :buildings="buildings"
            :multiple="multiple"
            :disabledItems="disabledItems"
            :buildingIsSelectable="buildingIsSelectable"
            :searchTerm="searchTerm"
            :selectedNodeIds="selectedNodeIds"
            @update:modelValue="handleUpdateFilters($event, multiple)"
          />
        </Tab>
        <Tab
          v-if="allowedTypes.includes('asset')"
          :label="$t('components.link_to_selector.type.assets')"
        >
          <LinksTree
            type="asset"
            :modelValue="modelValue"
            :buildingId="buildingId"
            :buildings="buildings"
            :multiple="multiple"
            :disabledItems="disabledItems"
            :buildingIsSelectable="buildingIsSelectable"
            :searchTerm="searchTerm"
            :selectedNodeIds="selectedNodeIds"
            @update:modelValue="handleUpdateFilters($event, multiple)"
          />
        </Tab>
        <Tab
          v-if="allowedTypes.includes('asset_group')"
          :label="$t('components.link_to_selector.type.asset_groups')"
        >
          <LinksTree
            type="asset_group"
            :modelValue="modelValue"
            :buildingId="buildingId"
            :buildings="buildings"
            :multiple="multiple"
            :disabledItems="disabledItems"
            :buildingIsSelectable="buildingIsSelectable"
            :searchTerm="searchTerm"
            :selectedNodeIds="selectedNodeIds"
            @update:modelValue="handleUpdateFilters($event, multiple)"
          />
        </Tab>
      </Tabs>
      <div
        v-show="
          _searching &&
          (!isEmpty(searchResults.spaces) ||
            !isEmpty(searchResults.assets) ||
            !isEmpty(searchResults.asset_groups))
        "
        class="p-3 md:px-6 bg-secondary h-[60dvh] md:h-[400px] max-h-[100dvh] overflow-y-auto flex flex-col grow gap-3 scrollbar-quaternary"
      >
        <div v-for="type in ['spaces', 'assets', 'asset_groups']" :key="type">
          <p
            v-if="searchResults[type] && searchResults[type].length"
            class="text-sm font-medium text-secondary mb-1.5"
          >
            {{ $t(`components.link_to_selector.type.${type}`) }}
          </p>
          <LinkToSelectorItem
            v-for="item in searchResults[type]"
            :key="item.id"
            :data="item"
            :level="1"
            :showState="false"
            :searchTerm="searchTerm"
            :selectable="
              (!buildingIsSelectable ? item.id !== item.buildingId : true) &&
              !disabledItems.includes(item.id)
            "
            :multiple="multiple"
            :isSelected="checkIfSelected(item.id)"
            :type="item.type"
            @updateState="
              handleUpdateFilters(
                {
                  node: item,
                  state: checkIfSelected(item.id)
                    ? multiple
                      ? 'all'
                      : 'single'
                    : 'empty',
                },
                multiple
              )
            "
            class="mb-1"
          />
        </div>
      </div>
      <div
        v-show="
          _searching &&
          isEmpty(searchResults.spaces) &&
          isEmpty(searchResults.assets) &&
          isEmpty(searchResults.asset_groups)
        "
        class="p-3 md:px-6 bg-secondary flex items-center justify-center grow w-full h-[60dvh] md:h-[400px] max-h-[100dvh] overflow-y-hidden"
      >
        <State
          :type="state.type"
          :title="state.title"
          :text="state.text"
          :button="
            showActionButton ? $t('components.buttons.clear_search') : undefined
          "
          @click:button="$emit('clear:search')"
        />
      </div>
    </div>
    <div class="w-full bg-secondary mt-auto">
      <Breadcrumb
        v-if="breadcrumbs"
        :items="breadcrumbs"
        size="md"
        type="text"
        isScrollable
        class="w-full bg-primary border-t border-secondary px-3 md:px-6 py-2 cursor-default"
      />
    </div>
  </div>
</template>

<script setup>
import { computed, watchEffect } from "vue";
import { isEmpty } from "lodash";
import { storeToRefs } from "pinia";
import { Breadcrumb } from "@/SharedComponents/Breadcrumbs";
import {
  LinksTree,
  LinkToSelectorItem,
} from "@/SharedComponents/LinkToSelector";
import { State, useState } from "@/SharedComponents/State";
import { Tabs, Tab } from "@/SharedComponents/Tabs";
import { useSpacesAndAssetsTree } from "@/Composables/useSpacesAndAssetsTree";
import { useLinkedToBreadcrumbs } from "@/Composables/useLinkedToBreadcrumbs";
import { cn } from "@/Utils/cn";

const emit = defineEmits([
  "update:modelValue",
  "update:assets",
  "update:includeAssets",
  "update:breadcrumbs",
  "clear:search",
]);

const props = defineProps({
  modelValue: { type: Object, default: () => ({}) },
  tab: { type: [String, Number], default: 0 },
  query: { type: String, default: "" },
  searching: { type: Boolean, default: undefined },
  buildingId: { type: String, required: false },
  buildings: { type: Array, default: () => [] },
  multiple: { type: Boolean, default: false },
  buildingIsSelectable: { type: Boolean, default: true },
  showBreadcrumbs: { type: Boolean, default: true },
  showActionButton: { type: Boolean, default: true },
  disabledItems: { type: Array, default: () => [] },
  allowedTypes: {
    type: Array,
    default: () => ["space", "asset", "asset_group"],
  },
  class: { type: null, required: false },
});

const _buildings = computed(() =>
  props.buildingId
    ? props.buildings.filter((building) => building.id === props.buildingId)
    : props.buildings
);

// Breadcrumbs
const { getBreadcrumbs } = useLinkedToBreadcrumbs();

const { searchTerm, searchByType, selection, handleUpdateFilters } =
  useSpacesAndAssetsTree(_buildings.value, null, props.modelValue, emit);

watchEffect(() => (searchTerm.value = props.query));

const _searching = computed(() => {
  return props.searching !== undefined
    ? props.searching
    : searchTerm.value !== "";
});

const searchResults = computed(() => {
  let results = {};

  if (props.allowedTypes.includes("space")) {
    results["spaces"] = searchByType("space");
  }

  if (props.allowedTypes.includes("asset")) {
    results["assets"] = searchByType("asset");
  }

  if (props.allowedTypes.includes("asset_group")) {
    results["asset_groups"] = searchByType("asset_group");
  }

  return results;
});

const checkIfSelected = (id) => selectedNodeIds.value.includes(id);

const selectedNodeIds = computed(() => {
  const ids = selection.value.buildings.concat(selection.value.children);
  return props.multiple ? ids : ids.slice(-1);
});

const breadcrumbs = computed(() => {
  if (props.multiple) return null;
  if (!props.showBreadcrumbs) return null;

  if (selectedNodeIds.value.length) {
    const parentNames = getBreadcrumbs(selectedNodeIds.value.at(-1));
    return parentNames.map((name) => ({ label: name }));
  }
  return null;
});

const stateStore = useState();
const { state } = storeToRefs(stateStore);
</script>
